import { PlanI } from '@app/modules/admin/providers/plan-n-pricing/plan-n-pricing.interfaces';
import { AuthService } from '@app/modules/auth';
import { INDEXSYMBOL } from '@app/_helpers';
import { AlgoPermissions } from '../../../../../../../../utProto/webEnums_pb';
import { LoginSource } from './personal-details.enum';
import { ExchangeId } from '../../../../../../../../utProto/enums_pb';
import { EXCHANGE_STRINGS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { PlanTypeEnum } from '@app/_shared/shared.enums';

export class PersonalDetails {
    address: string;
    brokers: string[];
    city: string;
    client_id: string;
    country: string;
    created_at: string;
    dob: string | number;
    email: string;
    extra_credit: number;
    full_name: string;
    is_active: boolean;
    login_source: LoginSource;
    login_type: string;
    pan: string;
    phone: string;
    plan: string;
    plan_id: number;
    next_plan_id: number;
    plan_start_time: string;
    role: string;
    state: string;
    image: string;
    algo_permissions: number[];
    ucc: string;
    pin_code: string;
    locality: string;
    gst_number: string;
    previous_plan: string;
    previous_plan_id: number;
    next_plan_id_value?:number;

    specialSymbolsPermissionMap: Map<string, boolean>;
    displayName: string;

    constructor(apiResponse: Object) {
        Object.keys(apiResponse).forEach((k) => {
            this[k] = apiResponse[k];
        });

        this.specialSymbolsPermissionMap = new Map<string, boolean>([
            [INDEXSYMBOL.NIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_NIFTY)],
            [INDEXSYMBOL.BANKNIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_BANKNIFTY)],
            [INDEXSYMBOL.FINNIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_FINNIFTY)],
            [INDEXSYMBOL.MIDCPNIFTY, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_MIDCAPNIFTY)],
            [INDEXSYMBOL.SENSEX, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_SENSEX)],
            [INDEXSYMBOL.BANKEX, this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_BANKEX)]
        ])

        // If this user has linked SI account, we will prioritize UCC (from SI account) to uTrade Algos client ID
        // for display purpose on header, profile and dashboard section.
        if (this?.ucc)
            this.displayName = this.ucc;
        else
            this.displayName = this.client_id;
    }

    isExternalStrategyAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_EXTERNALSTRATEGY);
    }

    isCreationAllowed(authService: AuthService) {
        return (authService.accessTokenObj.loginSource !== LoginSource.SWASTIKA) || (authService.accessTokenObj.loginSource === LoginSource.SWASTIKA) && this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_HIDECUSTOMPORTFOLIO)
    }

    isDownloadAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_DOWNLOADUPLOADPORTFOLIO)
    }

    isMaginCalculatorAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_MARGINCALCULATOR);
    }

    isPayoffCurveAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_PAYOFFCURVE);
    }

    isOneAiAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_AIASSISTANT_ONEAI);
    }
    
    isAiRecommendOriginalsAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_AIRECOMMEND_ORIGINALS);
    }

    isPublicPortFolioAllowed() {
        return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_PUBLICPORTFOLIO);
    }

    isSymbolAllowedInPlan(symbol: string, exchange: string = ExchangeId.EXCHANGEID_NSEFO.toString()) {

        if (this.specialSymbolsPermissionMap.has(symbol)) {
            return this.specialSymbolsPermissionMap.get(symbol);
        }
        if (exchange === ExchangeId.EXCHANGEID_NSEFO.toString()) {
            return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLNSESYMBOLS);
        } else {
            return this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLBSESYMBOLS);
        }
    }

    getSelectedSymbols() {
        const isNSE = this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLNSESYMBOLS);
        const isBSE = this.algo_permissions.includes(AlgoPermissions.WEBALGOPERMISSION_ALLBSESYMBOLS);
        if (isNSE && isBSE) {
            return null;
        } else if (isNSE) {
            return EXCHANGE_STRINGS[ExchangeId.EXCHANGEID_NSEFO];
        } else if (isBSE) {
            return EXCHANGE_STRINGS[ExchangeId.EXCHANGEID_BSEFO];
        } else {
            return null;
        }
    }
}

export interface PersonalDetailsUpdateResponse {
    address: string;
    city: string;
    client_id: string;
    country: string;
    dob: number;
    email: string;
    extra_credit: number;
    full_name: string;
    is_active: boolean;
    password: string;
    phone: string;
    plan: number;
    state: string;
}

export interface ImageUploadResponse {
    Image: string;
    Message: string;
}

export interface MyPlanInfoResponse {
    plan: PlanI;
    price: number;
    discount: number;
    subscription_end_date: string;
    subscription_start_date: string;
    subscription_status: string;
    subscription_payment_gateway: string;
    subscription_payment_mode: string;
    is_autopay_off: boolean;
    is_algo_provider?: boolean;
    insufficient_balance: boolean;
    is_recurring_success_pop_up_shown: boolean;
    is_connected_to_broker_before: boolean;
    special_coupon_code: string;
    plan_type: PlanTypeEnum;
    free_plan_extension_days: number
}

export interface ActiveSession {
    device: string;
    login_date: number;
    ip_address: string;
    session_id: string
    logout_date: number;
    access_type: number;
}

export interface IsContactRegisteredResponse {
    exists: boolean;
    message?: string;
    error?: string;
}

export interface UpdateProfileResponse {
    success: boolean;
    message?: string;
    error?: string;
    data?: {
        "email"?: string;
        "mobile_number"?: string;
        "expires_in": number;
        "attempt_count"?: number;
        "session_id"?: string;
    }
}

export interface VerifyInitialDetailsOtpResponse {
    success: boolean;
    message?: string;
    error?: string;
}

export interface ResendInitialDetailsOtpResponse {
    success: boolean;
    message?: string;
    error?: string;
    data?: {
        "email"?: string;
        "phone"?: string;
        "expires_in": number;
        "attempt_count"?: number;
    }
}